/**
 * Pickup slot picker.
 *
 * The available instants come from `available_slots`, which already applies the
 * restaurant's opening hours, its minimum lead time, its maximum booking window,
 * and its slot interval. This component only groups and renders them — it never
 * invents a time the kitchen has not offered.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  formatClock,
  formatDateKey,
  localDateKey,
  weekdayName,
  type Locale,
  type Translator,
} from '@yummy/shared';
import { Callout, LoadingState, Row, Stack, Text, ToneBadge, cn, useLocale } from '@yummy/ui-rnr';

export interface SlotPickerProps {
  slots: string[];
  timezone: string;
  value: string | null;
  onChange: (slot: string) => void;
  loading?: boolean;
  /**
   * The soonest pickup the current basket can make, as an instant, or null when
   * the quote has not arrived.
   *
   * Slots before it are still offered and still selectable — the kitchen may
   * well be quicker than the estimate — but they are marked, and the chip's
   * accessible name says why. Withholding them would be a gate; this is
   * deliberately a warning.
   */
  earliest?: string | null;
}

interface DayGroup {
  key: string;
  label: string;
  slots: string[];
}

/**
 * Groups ISO instants into local calendar days with a readable label.
 *
 * @param slots ISO timestamps.
 * @param timezone Restaurant timezone.
 * @param now Reference instant for Today/Tomorrow labels.
 * @param t Translator for the relative day labels.
 * @param locale Target locale for weekday and date names.
 * @returns Day groups in chronological order.
 */
function groupByDay(
  slots: string[],
  timezone: string,
  now: Date,
  t: Translator,
  locale: Locale,
): DayGroup[] {
  const todayKey = localDateKey(now, timezone);
  const groups = new Map<string, DayGroup>();

  for (const slot of slots) {
    const key = localDateKey(new Date(slot), timezone);
    let group = groups.get(key);

    if (!group) {
      group = { key, label: dayLabel(key, todayKey, t, locale), slots: [] };
      groups.set(key, group);
    }
    group.slots.push(slot);
  }

  return [...groups.values()];
}

function dayLabel(key: string, todayKey: string, t: Translator, locale: Locale): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  const today = new Date(`${todayKey}T00:00:00Z`);
  const diffDays = Math.round((date.getTime() - today.getTime()) / 86_400_000);

  if (diffDays === 0) return t('common.today');
  if (diffDays === 1) return t('common.tomorrow');

  return `${weekdayName(date.getUTCDay(), true, locale)} ${formatDateKey(key, 'short', locale)}`;
}

/**
 * Renders selectable pickup times.
 *
 * @param props Slots, timezone, current selection, and handler.
 * @returns The day tabs, the slot chips, and the selected day's summary.
 */
export function SlotPicker({ slots, timezone, value, onChange, loading, earliest }: SlotPickerProps) {
  const { t, locale } = useLocale();
  const [now] = useState(() => new Date());

  // The line the markers sit on. Both sides are instants, so the restaurant's
  // timezone and the device's never enter into the comparison.
  const earliestAt = earliest ? Date.parse(earliest) : null;
  const isTight = (slot: string) => earliestAt !== null && Date.parse(slot) < earliestAt;
  const anyTight = earliestAt !== null && slots.some(isTight);

  // Grouping is memoised on the slot list alone; re-deriving it on every tick of
  // the shared clock would be wasteful and could reorder chips mid-tap.
  const groups = useMemo(
    () => groupByDay(slots, timezone, now, t, locale),
    [slots, timezone, now, t, locale],
  );

  const [activeDay, setActiveDay] = useState<string | null>(null);

  /**
   * Follow the selection when it moves to another day.
   *
   * `activeDay` is browsing state: tapping a tab shows that day without moving
   * the choice, which is why it wins over the selection's own day below. But when
   * the *choice* moves — the checkout's "use this instead" link, or the default
   * following the estimate — the tab has to move with it, or the new time is
   * selected on a day nobody is looking at and the tap reads as having done
   * nothing at all.
   *
   * Only a change re-syncs. Browsing away from the selection must survive the
   * next render, so comparing against the previous value is the whole test.
   */
  const lastValue = useRef(value);
  useEffect(() => {
    if (value === lastValue.current) return;
    lastValue.current = value;
    if (value !== null) setActiveDay(localDateKey(new Date(value), timezone));
  }, [value, timezone]);

  const selectedDay =
    groups.find((group) => group.key === activeDay) ??
    groups.find((group) => value !== null && localDateKey(new Date(value), timezone) === group.key) ??
    groups[0];

  if (loading) return <LoadingState message={t('customer.checkout.findingSlots')} />;

  if (groups.length === 0) {
    return (
      <Callout
        tone="warning"
        title={t('customer.checkout.noSlots')}
        message={t('customer.checkout.noSlotsHint')}
        size="base"
      />
    );
  }

  return (
    <Stack gap="md">
      {groups.length > 1 ? (
        <Row gap="sm" wrap>
          {groups.map((group) => {
            const selected = group.key === selectedDay?.key;
            return (
              <Pressable
                key={group.key}
                onPress={() => setActiveDay(group.key)}
                accessibilityRole="button"
                className={cn(
                  'active:opacity-70 rounded-full border px-3 py-2',
                  selected ? 'border-primary bg-primary-soft' : 'border-border bg-card'
                )}>
                <Text
                  className={cn(
                    'text-sm font-semibold',
                    selected ? 'text-primary-strong' : 'text-muted-foreground'
                  )}>
                  {group.label}
                </Text>
              </Pressable>
            );
          })}
        </Row>
      ) : null}

      {/* The key sits above the grid rather than under it. The times it explains
          are the earliest ones, and with a five-minute interval the grid runs for
          pages, so a legend at its foot may as well not be there. */}
      {anyTight ? (
        <Row gap="xs" align="center">
          <View className="bg-warning h-1.5 w-1.5 rounded-full" />
          <Text className="text-muted-foreground text-xs">
            {t('customer.checkout.slotTooSoonKey')}
          </Text>
        </Row>
      ) : null}

      <Row gap="sm" wrap>
        {(selectedDay?.slots ?? []).map((slot) => {
          const selected = value === slot;
          const tight = isTight(slot);
          return (
            <Pressable
              key={slot}
              onPress={() => onChange(slot)}
              accessibilityRole="button"
              // The dot is decoration. This is the marker in words, because a
              // colour and a shape on their own are not a reason.
              accessibilityLabel={
                tight
                  ? t('customer.checkout.slotTooSoonChip', {
                      time: formatClock(slot, timezone),
                    })
                  : undefined
              }
              className={cn(
                'active:opacity-70 min-w-[76px] items-center rounded-md px-3 py-2',
                selected
                  ? 'border-primary bg-primary-soft border-2'
                  : tight
                    ? 'border-warning bg-card border'
                    : 'border-border bg-card border'
              )}>
              {tight ? (
                <View className="bg-warning absolute top-1 right-1.5 h-1.5 w-1.5 rounded-full" />
              ) : null}
              <Text
                className={cn(
                  'text-lg font-bold',
                  selected ? 'text-primary-strong' : 'text-foreground'
                )}>
                {formatClock(slot, timezone)}
              </Text>
            </Pressable>
          );
        })}
      </Row>

      {selectedDay && groups.length > 1 ? (
        <Row gap="sm" align="center">
          <ToneBadge label={selectedDay.label} tone="neutral" size="sm" />
          <Text className="text-subtle-foreground text-xs">
            {t('customer.checkout.slotTimesAvailable', { count: selectedDay.slots.length })}
          </Text>
        </Row>
      ) : null}
    </Stack>
  );
}
